// LoadingComp.js
import React, { useEffect, useState } from "react";
import { Box, keyframes, useTheme, alpha } from "@mui/material";
import { TranslateContext } from "./context/TranslateContext";

// ─── Keyframes ─────────────────────────────────────────────
const orbFloat = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1); }
  33%       { transform: translate(30px, -30px) scale(1.1); }
  66%       { transform: translate(-20px, 20px) scale(0.95); }
`;

const scanDown = keyframes`
  0%, 100% { top: 0%; opacity: 0; }
  10%       { opacity: 1; }
  90%       { opacity: 1; }
  100%      { top: 100%; opacity: 0; }
`;

const ambientFloat = keyframes`
  0%   { transform: translateY(100vh) rotate(0deg); opacity: 0; }
  10%  { opacity: 0.35; }
  90%  { opacity: 0.35; }
  100% { transform: translateY(-10vh) rotate(720deg); opacity: 0; }
`;

const blink = keyframes`
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.3; }
`;

const shimmer = keyframes`
  0%   { left: -80%; }
  100% { left: 160%; }
`;

const fadeSlideUp = keyframes`
  0%   { opacity: 0; transform: translateY(12px); }
  100% { opacity: 1; transform: translateY(0); }
`;

// The magic — clip-path reveals logo bottom → top
const fillReveal = keyframes`
  0%   { clip-path: inset(100% 0% 0% 0%); }
  100% { clip-path: inset(0% 0% 0% 0%); }
`;

const progressAnim = keyframes`
  0%   { width: 0%;   opacity: 0.6; }
  50%  { width: 75%;  opacity: 1;   }
  100% { width: 100%; opacity: 0.6; }
`;

// ─── Status Messages ───────────────────────────────────────
const STATUS_MESSAGES = [
  "Initializing neural networks",
  "Loading language models",
  "Calibrating attention layers",
  "Optimizing inference engine",
  "Establishing secure connection",
];

// ─── Ambient Particle ──────────────────────────────────────
const AmbientParticle = ({ delay, left, duration, size }) => (
  <Box
    sx={{
      position: "absolute",
      left: `${left}%`,
      bottom: 0,
      width: `${size}px`,
      height: `${size}px`,
      borderRadius: "50%",
      background: (theme) => theme.palette.primary.main,
      opacity: 0,
      animation: `${ambientFloat} ${duration}s linear infinite`,
      animationDelay: `${delay}s`,
      pointerEvents: "none",
    }}
  />
);

// ─── Main Component ───────────────────────────────────────
const LoadingComp = () => {
  const theme = useTheme();
  const { lang } = React.useContext(TranslateContext);
  const isDark = theme.palette.mode === "dark";

  const [statusIndex, setStatusIndex] = useState(0);
  const [statusVisible, setStatusVisible] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setStatusVisible(false);
      setTimeout(() => {
        setStatusIndex((prev) => (prev + 1) % STATUS_MESSAGES.length);
        setStatusVisible(true);
      }, 300);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  // Deterministic particles — no Math.random() to avoid re-render jitter
  const particles = Array.from({ length: 18 }, (_, i) => ({
    id: i,
    left: (i * 37 + 11) % 100,
    duration: ((i * 7) % 10) + 12,
    delay: (i * 1.7) % 10,
    size: ((i * 3) % 3) + 1,
  }));

  const primary = theme.palette.primary.main;
  const secondary = theme.palette.secondary.main;
  const info = theme.palette.info?.main || "#06b6d4";

  const logoSrc = isDark
    ? "/assets/codeyon-logo-white.webp"
    : "/assets/codeyon-logo-dark.webp";

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: isDark ? "#0a0a14" : "#f8f8ff",
        overflow: "hidden",
      }}
    >
      {/* ── Background Orbs ── */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          overflow: "hidden",
          pointerEvents: "none",
        }}
      >
        {[
          { color: primary, size: 500, top: "-10%", left: "-5%", delay: "0s" },
          {
            color: secondary,
            size: 400,
            bottom: "-10%",
            right: "-5%",
            delay: "-3s",
          },
          { color: info, size: 300, top: "40%", left: "50%", delay: "-5s" },
        ].map((blob, i) => (
          <Box
            key={i}
            sx={{
              position: "absolute",
              width: blob.size,
              height: blob.size,
              borderRadius: "50%",
              background: `radial-gradient(circle, ${alpha(blob.color, isDark ? 0.15 : 0.07)} 0%, transparent 70%)`,
              top: blob.top,
              left: blob.left,
              bottom: blob.bottom,
              right: blob.right,
              filter: "blur(80px)",
              animation: `${orbFloat} 8s ease-in-out infinite`,
              animationDelay: blob.delay,
            }}
          />
        ))}
      </Box>

      {/* ── Grid ── */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          backgroundImage: `
            linear-gradient(${alpha(primary, 0.04)} 1px, transparent 1px),
            linear-gradient(90deg, ${alpha(primary, 0.04)} 1px, transparent 1px)
          `,
          backgroundSize: "60px 60px",
          maskImage:
            "radial-gradient(ellipse at center, black 30%, transparent 70%)",
          WebkitMaskImage:
            "radial-gradient(ellipse at center, black 30%, transparent 70%)",
        }}
      />

      {/* ── Scan Line ── */}
      <Box
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          height: "1px",
          background: `linear-gradient(90deg, transparent, ${alpha(primary, 0.3)}, transparent)`,
          animation: `${scanDown} 4s ease-in-out infinite`,
          pointerEvents: "none",
          zIndex: 20,
        }}
      />

      {/* ── Particles ── */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          zIndex: 5,
        }}
      >
        {particles.map((p) => (
          <AmbientParticle key={p.id} {...p} />
        ))}
      </Box>

      {/* ════════════════════════════════════════
          LOGO FILL — the whole trick is here
          Ghost image + clip-path animated copy
      ════════════════════════════════════════ */}
      <Box
        sx={{
          position: "relative",
          zIndex: 10,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          animation: `${fadeSlideUp} 0.7s ease both`,
        }}
      >
        {/* Logo wrapper — fixed size, nothing leaks out */}
        <Box
          sx={{
            position: "relative",
            width: { xs: 200, sm: 260 },
            height: { xs: 64, sm: 80 },
            flexShrink: 0,
          }}
        >
          {/* ── Ghost layer (dim outline) ── */}
          <Box
            component="img"
            src={logoSrc}
            alt=""
            draggable={false}
            sx={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain",
              opacity: isDark ? 0.1 : 0.07,
              userSelect: "none",
            }}
          />

          {/* ── Filled layer — clip-path does the reveal ── */}
          <Box
            component="img"
            src={logoSrc}
            alt="Codeyon"
            draggable={false}
            sx={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain",
              userSelect: "none",
              // ✅ clip-path inset: top shrinks 100→0, revealing bottom→top
              animation: `${fillReveal} 2.8s cubic-bezier(0.4, 0, 0.2, 1) infinite`,
              filter: isDark
                ? `drop-shadow(0 0 10px ${alpha(primary, 0.9)}) drop-shadow(0 0 20px ${alpha(secondary, 0.5)})`
                : `drop-shadow(0 0 8px ${alpha(primary, 0.7)})`,
            }}
          />

          {/* ── Shimmer sweep ── */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              overflow: "hidden",
              pointerEvents: "none",
              "&::after": {
                content: '""',
                position: "absolute",
                top: 0,
                bottom: 0,
                width: "40%",
                background: `linear-gradient(90deg, transparent, ${alpha("#fff", 0.2)}, transparent)`,
                animation: `${shimmer} 2.8s ease-in-out infinite`,
                animationDelay: "0.3s",
              },
            }}
          />
        </Box>

        {/* ── Subtitle ── */}
        <Box
          sx={{
            mt: 2,
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          {[0, 1].map((i) => (
            <Box
              key={i}
              sx={{
                width: 3,
                height: 3,
                borderRadius: "50%",
                background:
                  i === 0
                    ? `linear-gradient(135deg, ${primary}, ${secondary})`
                    : `linear-gradient(135deg, ${secondary}, ${info})`,
                animation: `${blink} 2s ease-in-out infinite`,
                animationDelay: `${i * 0.4}s`,
              }}
            />
          ))}
          <Box
            component="span"
            sx={{
              fontSize: "11px",
              fontWeight: 600,
              letterSpacing: "3px",
              textTransform: "uppercase",
              color: alpha(theme.palette.text.primary, 0.35),
            }}
          >
            {lang?.subtitle || "AI-powered conversations"}
          </Box>
          {[2, 3].map((i) => (
            <Box
              key={i}
              sx={{
                width: 3,
                height: 3,
                borderRadius: "50%",
                background: `linear-gradient(135deg, ${info}, ${primary})`,
                animation: `${blink} 2s ease-in-out infinite`,
                animationDelay: `${i * 0.3}s`,
              }}
            />
          ))}
        </Box>
      </Box>

      {/* ── Progress Bar ── */}
      <Box sx={{ mt: 5, width: { xs: 220, sm: 280 }, zIndex: 10 }}>
        <Box
          sx={{
            width: "100%",
            height: "2px",
            backgroundColor: alpha(
              theme.palette.common.white,
              isDark ? 0.06 : 0.25,
            ),
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              height: "100%",
              background: `linear-gradient(90deg, ${primary}, ${secondary}, ${info})`,
              borderRadius: 2,
              boxShadow: `0 0 8px ${alpha(primary, 0.6)}`,
              animation: `${progressAnim} 2.5s ease-in-out infinite`,
            }}
          />
        </Box>

        {/* Status */}
        <Box
          sx={{
            mt: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "6px",
            opacity: statusVisible ? 1 : 0,
            transition: "opacity 0.3s ease",
          }}
        >
          <Box
            sx={{
              width: 3,
              height: 3,
              borderRadius: "50%",
              background: info,
              animation: `${blink} 1.5s ease-in-out infinite`,
            }}
          />
          <Box
            component="span"
            sx={{
              fontSize: "10px",
              fontWeight: 500,
              letterSpacing: "1.5px",
              textTransform: "uppercase",
              color: alpha(theme.palette.text.primary, 0.3),
            }}
          >
            {lang?.loadingStatus || STATUS_MESSAGES[statusIndex]}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default LoadingComp;
